import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ConversationAISummary } from '../types/customers';

const AGENT_NAME_KEY = 'conversation_agent_name';

interface ConversationState {
  // 新消息标记 (conversationId Set)
  newMessageIds: Set<string>;

  // 可加入状态的对话 (首次 pending 通知)
  pendingJoinIds: Set<string>;

  // 当前选中的对话
  selectedConversationId: string | null;

  // 客服会话信息
  agentName: string | null;
  joinedConversationId: string | null;
  joinedAt: string | null;

  // 摘要数据缓存
  summaryCache: Map<string, ConversationAISummary>;

  // 刷新触发器 (用于触发列表/消息/ticket刷新)
  listRefreshTrigger: number;
  messageRefreshTrigger: number;
  ticketRefreshTrigger: number;

  // Actions - 新消息标记
  markAsNew: (conversationId: string) => void;
  clearNewMark: (conversationId: string) => void;
  clearAllNewMarks: () => void;

  // Actions - 可加入状态
  markAsPendingJoin: (conversationId: string) => void;
  clearPendingJoin: (conversationId: string) => void;

  // Actions - 对话选择
  setSelectedConversation: (conversationId: string | null) => void;

  // Actions - 客服会话
  setAgentName: (name: string) => void;
  joinConversation: (conversationId: string, agentName: string) => void;
  leaveConversation: () => void;
  isJoinedToConversation: (conversationId: string) => boolean;

  // Actions - 摘要缓存
  cacheSummary: (conversationId: string, summary: ConversationAISummary) => void;
  getCachedSummary: (conversationId: string) => ConversationAISummary | undefined;
  clearSummaryCache: () => void;

  // Actions - 刷新触发
  triggerListRefresh: () => void;
  triggerMessageRefresh: () => void;
  triggerTicketRefresh: () => void;

  // Actions - 判断是否应该自动刷新消息
  shouldAutoRefreshMessages: (conversationId: string) => boolean;

  // Actions - 清除客服名字
  clearAgentName: () => void;

  // Actions - 重置
  reset: () => void;
}

export const useConversationStore = create<ConversationState>()(
  persist(
    (set, get) => ({
      // Initial state
      newMessageIds: new Set(),
      pendingJoinIds: new Set(),
      selectedConversationId: null,
      agentName: null,
      joinedConversationId: null,
      joinedAt: null,
      summaryCache: new Map(),
      listRefreshTrigger: 0,
      messageRefreshTrigger: 0,
      ticketRefreshTrigger: 0,

      // 新消息标记 actions
      markAsNew: (conversationId: string) => {
        set(state => ({
          newMessageIds: new Set([...state.newMessageIds, conversationId]),
        }));
      },

      clearNewMark: (conversationId: string) => {
        set(state => {
          const newSet = new Set(state.newMessageIds);
          newSet.delete(conversationId);
          return { newMessageIds: newSet };
        });
      },

      clearAllNewMarks: () => {
        set({ newMessageIds: new Set() });
      },

      // 可加入状态 actions
      markAsPendingJoin: (conversationId: string) => {
        set(state => ({
          pendingJoinIds: new Set([...state.pendingJoinIds, conversationId]),
        }));
      },

      clearPendingJoin: (conversationId: string) => {
        set(state => {
          const newSet = new Set(state.pendingJoinIds);
          newSet.delete(conversationId);
          return { pendingJoinIds: newSet };
        });
      },

      // 对话选择 actions
      setSelectedConversation: (conversationId: string | null) => {
        const state = get();

        // 如果选择新对话，清除其新消息标记
        if (conversationId && state.newMessageIds.has(conversationId)) {
          set(state => {
            const newSet = new Set(state.newMessageIds);
            newSet.delete(conversationId);
            return {
              selectedConversationId: conversationId,
              newMessageIds: newSet,
            };
          });
        } else {
          set({ selectedConversationId: conversationId });
        }
      },

      // 客服会话 actions
      setAgentName: (name: string) => {
        set({ agentName: name });
        // 同时保存到 localStorage (persist 只保存部分字段)
        try {
          localStorage.setItem(AGENT_NAME_KEY, name);
        } catch (error) {
          console.warn('Failed to save agent name to localStorage:', error);
        }
      },

      joinConversation: (conversationId: string, agentName: string) => {
        set({
          joinedConversationId: conversationId,
          agentName: agentName,
          joinedAt: new Date().toISOString(),
        });

        // 保存客服名字到 localStorage
        try {
          localStorage.setItem(AGENT_NAME_KEY, agentName);
        } catch (error) {
          console.warn('Failed to save agent name to localStorage:', error);
        }
      },

      leaveConversation: () => {
        set({
          joinedConversationId: null,
          joinedAt: null,
        });
      },

      isJoinedToConversation: (conversationId: string) => {
        return get().joinedConversationId === conversationId;
      },

      // 摘要缓存 actions
      cacheSummary: (conversationId: string, summary: ConversationAISummary) => {
        set(state => {
          const newCache = new Map(state.summaryCache);
          newCache.set(conversationId, summary);
          return { summaryCache: newCache };
        });
      },

      getCachedSummary: (conversationId: string) => {
        return get().summaryCache.get(conversationId);
      },

      clearSummaryCache: () => {
        set({ summaryCache: new Map() });
      },

      // 刷新触发 actions
      triggerListRefresh: () => {
        const currentTrigger = get().listRefreshTrigger;
        console.log('Store: triggerListRefresh called, current:', currentTrigger, 'next:', currentTrigger + 1);
        set(state => ({
          listRefreshTrigger: state.listRefreshTrigger + 1,
        }));
      },

      triggerMessageRefresh: () => {
        set(state => ({
          messageRefreshTrigger: state.messageRefreshTrigger + 1,
        }));
      },

      triggerTicketRefresh: () => {
        console.log('Store: triggerTicketRefresh called');
        set(state => ({
          ticketRefreshTrigger: state.ticketRefreshTrigger + 1,
        }));
      },

      // 判断是否应该自动刷新消息
      shouldAutoRefreshMessages: (conversationId: string) => {
        return get().selectedConversationId === conversationId;
      },

      // 清除客服名字（用于系统登出）
      clearAgentName: () => {
        set({ agentName: null });
        try {
          localStorage.removeItem(AGENT_NAME_KEY);
        } catch (error) {
          console.warn('Failed to remove agent name from localStorage:', error);
        }
      },

      // 重置
      reset: () => {
        set({
          newMessageIds: new Set(),
          pendingJoinIds: new Set(),
          selectedConversationId: null,
          joinedConversationId: null,
          joinedAt: null,
          summaryCache: new Map(),
          listRefreshTrigger: 0,
          messageRefreshTrigger: 0,
          ticketRefreshTrigger: 0,
          // 保留 agentName，因为它应该跨会话持久化
        });
      },
    }),
    {
      name: 'conversation-storage',
      partialize: state => ({
        // 只持久化客服名字
        agentName: state.agentName,
      }),
      onRehydrateStorage: () => state => {
        if (state) {
          // 尝试从 localStorage 恢复客服名字
          try {
            const savedName = localStorage.getItem(AGENT_NAME_KEY);
            if (savedName && !state.agentName) {
              state.agentName = savedName;
            }
          } catch (error) {
            console.warn('Failed to restore agent name from localStorage:', error);
          }

          // 确保 Set 和 Map 正确初始化
          state.newMessageIds = new Set();
          state.pendingJoinIds = new Set();
          state.summaryCache = new Map();
        }
      },
    }
  )
);
